
import React, {PropTypes, Component} from 'react';
import ReactDOM from 'react-dom';
import { Link, browserHistory } from 'react-router';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { SetStationNum, SetForecastName, SetForecastType, SetModalVisible } from '../../actions';
import AnchoredComponent from '../AnchoredComponent';
import AutoWidthSelect from '../AutoWidthSelect';
import DownloadForm from '../DownloadForm';
import BarChart from '../BarChart';
import ComponentIcons from '../ComponentIcons';
import PresetButtons from '../PresetButtons';
import Modal from '../Modal';
import styles from './Forecast.css';
import { DL_SERVICE_URL } from '../../config';
import { simplePlaceName, sameLocationNewHash, downloadImage, capitalize } from '../../utils/utils';
import withStyles from '../../decorators/withStyles';
import { downloadFile, gaEvent, xlt } from "../../utils/utils";
import {SetModalTitle} from "../../actions";
import {isUS} from "../../utils/utils";
import {SetControlsEnabled} from "../../actions";
import * as d3 from 'd3';
import { pngIcon, xlsIcon } from "../../icons";


class Forecast extends AnchoredComponent {

  static propTypes = {
  };

  constructor(props) {
    super(props);
    this.state = {
      dlType: "forecast-xls",
      settingsOpen: false,
      downloadOpen: false,
      floodDetailsOpen: false,
      modelGroupOpen: "NOAA 2012"
    };

    // TODO: consider making these official props.
    this.margin = {top: 20, right: 20, bottom: 60, left: 75};
    this.width = 780 - this.margin.left - this.margin.right;
    this.height = 340 - this.margin.top - this.margin.bottom;
  }

  setTxContent = el => {
    d3.select(el).attr("tx-content", "block");
  };

  openInfo = e => {
    gaEvent('modal', 'open', 'info-forecast');
    browserHistory.push({pathname: "/about", hash: "#about-risks-when",
      state: {modal: true, modalTitle: "About this site", returnTo: location.pathname}}
    );
  };

  openFloodDetails = e => {
    e.preventDefault();
    this.closeAll();
    gaEvent('modal', 'open', 'flood-details');
    this.props.dispatch(SetModalVisible(true));
    this.setState({floodDetailsOpen: true});
  };

  closeFloodDetails = e => {
    this.props.dispatch(SetModalVisible(false));
    this.setState({floodDetailsOpen: false});
  };

  openSettings = hashTarget => {
    this.closeAll();
    gaEvent('modal', 'open', 'settings-forecast');
    this.props.dispatch(SetModalVisible(true));
    this.setState({settingsOpen: true}, () => {
      if (hashTarget) {
        try {
          const el = ReactDOM.findDOMNode(this).querySelector(hashTarget);
          if (el) {
            setTimeout(() => {
              el.scrollIntoView();
            }, 100);
          }
        } catch (_) {}
      }
    });
  };

  closeSettings = e => {
    this.props.dispatch(SetModalVisible(false));
    this.setState({settingsOpen: false});
  };

  openDownload = e => {
    this.closeAll();
    this.props.dispatch(SetModalVisible(true));
    this.setState({downloadOpen: true})
  };

  closeDownload = e => {
    this.props.dispatch(SetModalVisible(false));
    this.setState({downloadOpen: false});
  };

  closeAll = () => {
    this.props.dispatch(SetModalVisible(false));
    this.setState({downloadOpen: false, settingsOpen: false, floodDetailsOpen: false});
  };

  handleForecastNameChange = e => {
    const value = e instanceof Object ? e.target.value : e;
    gaEvent('configure', 'forecastName', value);
    this.props.dispatch(SetForecastName(value));
    if (value !== "Multi-year" && value !== "Single-year") {
      this.props.dispatch(SetControlsEnabled(false));
    } else {
      this.props.dispatch(SetControlsEnabled(true));
    }
  };

  handleModelClick = (group) => {
    this.setState({modelGroupOpen: group});
  };

  handleModelSelection = e => {
    gaEvent('configure', 'forecastType', e.target.value);
    this.props.dispatch(SetForecastType(e.target.value));
  };

  handleStationChange = e => {
    const stnNum = parseInt(e.target.value);
    gaEvent('configure', 'station', this.props.stations[stnNum]);
    this.props.dispatch(SetStationNum(stnNum));
  };

  ssTarget() {
    const { placeType, placeID, waterLevel, forecastType, forecastName, stationNum } = this.props;
    const lang = Transifex.live.getSelectedLanguageCode();
    return `http://riskfinder.climatecentral.org/embed/forecast-img/${placeType}/${placeID}?level=${waterLevel.level}&unit=${waterLevel.unit}&forecastType=${forecastType}&forecastName=${forecastName}&stationNum=${stationNum}&lang=${lang}`;
  }

  dlCallback = (onDlComplete) => {
    const { placeType, placeID, forecastType, stationNum } = this.props;
    var url;
    if (this.state.dlType === "forecast-png") {
      const filename = `surging-seas-forecast-${placeID}.png`;
      url = DL_SERVICE_URL + "/screenshot" + `?filename=${filename}&width=1024&height=768&url=` + encodeURIComponent(this.ssTarget());
      downloadImage(url, "riskfinder.png", onDlComplete);
    } else if (this.state.dlType === "forecast-pptx") {
      const filename = `surging-seas-forecast-${placeID}.pptx`;
      url = DL_SERVICE_URL + "/screenshot" + `?filename=${filename}&format=pptx&width=1024&height=768&url=` + encodeURIComponent(this.ssTarget());
      downloadFile(url, "riskfinder.pptx", onDlComplete);
    } else if (this.state.dlType === "forecast-xls") {
      // Add artificial delay so that people will look at our pre-download advertisements.
      setTimeout(() => {

      }, 5 * 1000);
      const link = document.createElement('a');
      link.href = `/api/forecast/${placeType}/${placeID}/${forecastType}?type=${this.state.dlType}&stationNum=${stationNum}&slrdb=true`;
      link.download = `${placeID}-forecast.txt`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      //link.parentNode.removeChild(link);
      if (onDlComplete) {
        onDlComplete(null, link.href);
      }
    }
  };

  scenarioExplanatoryText(scenario, text) {
    const elements = scenario.split("_");
    const model = elements[0];
    const descriptions = {
      NRC: {
        'Lower': 'Corresponds to low future levels of heat-trapping pollution',
        'Middle': 'Corresponds to intermediate future levels of heat-trapping pollution',
        'Upper': 'Aims to reflect uncut pollution plus accelerated ice sheet decay (adding ice loss dynamics going beyond the historic trends extended for the lower and middle projections)',
      },
      NCA: {
        'Lowest': '21st century experiences same sea level rise as 20th century did (Note: the rate since 1992 is already roughly double the 20th century rate)',
        'Intermediate low': 'Corresponds to low future levels of heat-trapping pollution and moderate climate and sea level sensitivity',
        'Intermediate high': 'Corresponds to high future levels of heat-trapping pollution and high climate and sea level sensitivity',
        'Highest': 'Aims to reflect uncut pollution and maximum ice sheet loss rates',
      },
      ACE: {
        'Lower': '21st century experiences same sea level rise as 20th century did (Note: the rate since 1992 is already roughly double the 20th century rate)',
        'Middle': 'Aims to reflect an intermediate sea level rise scenario',
        'Upper': 'Aims to reflect uncut pollution and rapid ice sheet decay',
      },
    };
    if (descriptions[model]) {
      return descriptions[model][text] || '';
    } else {
      return '';
    }
  }

  setDlType(dlType) {
    if (dlType === "forecast-png") {
      this.props.dispatch(SetModalTitle("Get image"));
    } else if (dlType === "forecast-pptx") {
      this.props.dispatch(SetModalTitle("Get slide"));
    } else if (dlType === "forecast-xls") {
      this.props.dispatch(SetModalTitle("Get spreadsheet"));
    }
    this.setState({dlType: dlType});
  }

  modelGroupOpen(key) {
    return this.state.modelGroupOpen === key;
  }

  handleDownloadClick = (dlType) => {
    gaEvent('modal', 'open', `download-${dlType}`);
    this.setDlType(dlType);
    this.openDownload();
  };

  render() {
    const { data, metadata, stations, stationNum, place, placeName, waterLevel, graphTypes,
            forecastName, forecastType, getGraphValues, chartExplain } = this.props;
    const station = stations[stationNum];
    const distUnit = station.dist_unit === "km" ? "kilometers" : "miles";
    const isWaterLevel = forecastName !== "Multi-year" && forecastName !== "Single-year";
    const slrModelDescription = isWaterLevel ? data.forecastSummary.hardwired12 : data.forecastSummary.hardwired36;

    var yMax;
    if (waterLevel.unit === "m") {
      yMax = 5;
    } else {
      if (forecastType.startsWith("NOAA2017")) {
        yMax = 45;
      } else {
        yMax = 15;
      }
    }

    const isChecked = name => forecastName === name;
    const modelChecked = name => forecastType === name;
    const stnShortName = name => name.split("-")[0].trim();

    return (
      <div className="Forecast main">

        <div className="header">
          <a className="anchor" id="threat-forecast"/>
          <Link className="hash-link" to={sameLocationNewHash("#threat-forecast")}>
            <h1>When Are the Risks? <span className="anchor-hash">#</span></h1>
          </Link>
          <ComponentIcons tutorialUrl="https://www.youtube.com/watch?v=fvih4O9b1EQ" hasDownload={false} openSettings={this.openSettings} openDownload={() => {this.setDlType("forecast-pptx"); this.openDownload()}} openInfo={this.openInfo} />
        </div>

        <div className="state-content">

          <div className="graph-title">
            <h5 className="supertitle" ref={this.setTxContent}><var>{xlt(simplePlaceName(placeName).toUpperCase())}</var> AREA*</h5>
            <span>
              <h2>
                <AutoWidthSelect options={graphTypes} defaultValue={forecastName} handleChange={this.handleForecastNameChange}/>
                <svg className="download-icon png" version="1.1" viewBox="0 0 585.918 585.918" onClick={this.handleDownloadClick.bind(null, "forecast-png")}>
                  {pngIcon}
                </svg>
                <svg className="download-icon xls" version="1.1" viewBox="0 0 585.918 585.918" onClick={this.handleDownloadClick.bind(null, "forecast-xls")}>
                  {xlsIcon}
                </svg>
              </h2>
            </span>
          </div>
          <h3 className="graph-title">{this.props.subtitles[forecastName]}</h3>
          <div id="location" className="projection_chart_container">
            <BarChart
              data={getGraphValues(data, forecastName, waterLevel, this.props.textMap[forecastName])}
              waterLevel={waterLevel}
              isWaterLevel={isWaterLevel}
              yMax={isWaterLevel ? yMax : 1}
              margin={this.margin}
              width={this.width}
              height={this.height}
              xLabel={xlt(forecastName === "Multi-year" ? "End year" : "Year")}
              yLabel={xlt(isWaterLevel ? `Water Level (${waterLevel.unit.replace("m", "meters")})` : "Risk (% Likelihood)")}
            >
              <PresetButtons forecastPresets={metadata.quickpicks} handleAdvancedClick={this.openSettings.bind(null, "#select-model")} />
            </BarChart>
          </div>
          <p className="map-explain">
            <span ref={this.setTxContent}>
              *At <var>{xlt(stnShortName(station.name))}</var> water level
              station, <var>{station.dist}</var> {distUnit} from <var>{xlt(simplePlaceName(placeName.split(",")[0]))}</var>&nbsp;
                <Link to={{pathname: "/about", hash: "#about-nearby-locations", state: {modal: true, modalTitle: "About this site",
                                                                        returnTo: location.pathname}}}>
                  <span className="icon question-mark" />
                </Link>
                <svg onClick={this.openSettings.bind(null, "#select-station")} className="icon settings" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 8 8">
                  <path d="M3.5 0l-.5 1.19c-.1.03-.19.08-.28.13l-1.19-.5-.72.72.5 1.19c-.05.1-.09.18-.13.28l-1.19.5v1l1.19.5c.04.1.08.18.13.28l-.5 1.19.72.72 1.19-.5c.09.04.18.09.28.13l.5 1.19h1l.5-1.19c.09-.04.19-.08.28-.13l1.19.5.72-.72-.5-1.19c.04-.09.09-.19.13-.28l1.19-.5v-1l-1.19-.5c-.03-.09-.08-.19-.13-.28l.5-1.19-.72-.72-1.19.5c-.09-.04-.19-.09-.28-.13l-.5-1.19h-1zm.5 2.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5-1.5-.67-1.5-1.5.67-1.5 1.5-1.5z"/>
                </svg>
            </span>
          </p>
          <p className="caption">
            {slrModelDescription}&nbsp;
            <Link to={{pathname: "/about", hash: "#about-models-scenarios", state: {modal: true, modalTitle: "About this site",
                                                                        returnTo: location.pathname}}}>
              <span className="icon question-mark" />
            </Link>
            <svg onClick={this.openSettings.bind(null, "#select-model")} className="icon settings" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 8 8">
              <path d="M3.5 0l-.5 1.19c-.1.03-.19.08-.28.13l-1.19-.5-.72.72.5 1.19c-.05.1-.09.18-.13.28l-1.19.5v1l1.19.5c.04.1.08.18.13.28l-.5 1.19.72.72 1.19-.5c.09.04.18.09.28.13l.5 1.19h1l.5-1.19c.09-.04.19-.08.28-.13l1.19.5.72-.72-.5-1.19c.04-.09.09-.19.13-.28l1.19-.5v-1l-1.19-.5c-.03-.09-.08-.19-.13-.28l.5-1.19-.72-.72-1.19.5c-.09-.04-.19-.09-.28-.13l-.5-1.19h-1zm.5 2.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5-1.5-.67-1.5-1.5.67-1.5 1.5-1.5z"/>
            </svg>
            <span>{chartExplain(data, forecastName, stnShortName(station.name))}</span> {forecastName !== "Basic" ? <Link to={{pathname: "/about", hash: "#about-nearby-locations", state: {modal: true, modalTitle: "About this site", returnTo: location.pathname}}}>Key notes</Link> : null}
          </p>
        </div>

        <Modal isOpen={this.state.floodDetailsOpen && this.props.modalVisible} handleClick={this.closeFloodDetails}
               title="Key notes">
          <p>
            {(forecastName === "Multi-year" || forecastName === "Single-year") ?
              "Flood risks are developed from sea level projections combined with extreme water level risk statistics based on analysis of historic hourly water level records. " +
              "This method conservatively assumes no future changes in storm patterns. Some climate studies project an increased risk of large storm surges."
              : null
            }
            {(forecastName === "With mild flood" ||  forecastName === "With moderate flood" || forecastName === "With extreme flood") ?
              `${capitalize(forecastName.replace("extreme", "major").split(" ")[1])} flood height is based on analysis of historic hourly water level records. ` +
              "This method conservatively assumes no future changes in storm patterns. Some climate studies project an increased risk of large storm surges."
              : null
            }
          </p>
          <p>
            Nearby areas such as {stnShortName(station.name)} {simplePlaceName(placeName) !== stnShortName(station.name) ? "and " + simplePlaceName(placeName) : null} can have different
            likelihoods for the same flood height due to a variety of factors, such as differing local terrain. Local sea level rise
            should be more consistent across nearby areas, but may also vary due to factors such as local rates of land subsidence.
            You may use settings to explore regional consistency of flood risk and sea level rise at other nearby water level stations by selecting them.
          </p>
        </Modal>

        <Modal isOpen={this.state.settingsOpen && this.props.modalVisible} handleClick={this.closeSettings}
               title="Forecast settings">
          <h2>Select a chart type</h2>
          <label title="Chance of flood from now until year X">
            <input name="projection-type" type="radio" id="total" value="Multi-year" onChange={this.handleForecastNameChange} checked={isChecked("Multi-year")} />
            Multi-year flood risk</label>
          <label title="Chance of flood within year X">
            <input name="projection-type" type="radio" id="annual" value="Single-year" onChange={this.handleForecastNameChange} checked={isChecked("Single-year")} />
            Single-year flood risk</label>
          <label>
            <input name="projection-type" type="radio" id="slr-basic" value="Basic" onChange={this.handleForecastNameChange} checked={isChecked("Basic")} />
            Sea level rise only</label>
          <label>
            <input name="projection-type" type="radio" id="slr-mild-flood" value="With mild flood" onChange={this.handleForecastNameChange} checked={isChecked("With mild flood")}/>
            Sea level rise + height of mild flood</label>
          <label>
            <input name="projection-type" type="radio" id="slr-moderate-flood" value="With moderate flood" onChange={this.handleForecastNameChange} checked={isChecked("With moderate flood")} />
            Sea level rise + height of moderate flood</label>
          {isUS(place.id) ?
          <label>
            <input name="projection-type" type="radio" id="slr-extreme-flood" value="With extreme flood" onChange={this.handleForecastNameChange} checked={isChecked("With extreme flood")} />
            Sea level rise + height of extreme flood</label>
              : null}

          <h2 id="select-model">Select a sea level rise model</h2>
          <p>
            These advanced settings give access to multiple models from the continuously evolving scientific and
            technical literature. {isUS(place.id) ? `Note: Only NOAA 2017 takes account of recent research suggesting high Antarctic
            ice sheet sensitivity to warming (DeConto and Pollard 2016, Nature).` : ""}
          </p>
          {metadata.models.filter(model => model.name !== "Semi-empirical (2009)" && !(!isUS(place.id) && model.name === "NOAA (2017)")).map(model => {
            return (
              <div className={`tree ${this.modelGroupOpen(model.name) ? "open" : ""}`} key={model.name}>
                <h4 className="tree" onClick={this.handleModelClick.bind(this, model.name)}>
                  {this.modelGroupOpen(model.name) ? "-" : "+"} {model.name}
                </h4>
                {this.modelGroupOpen(model.name) ?
                  <div>
                    <p className="tree2">{model.description.replace(/\(Earth.s Future\)/, "(Earth\u2019s Future)")}</p>
                    {typeof model.settings[0] !== "object" ?
                      <p className="tree2 group-header" title={model.description}>{model.settings_title}</p>
                      : null
                    }
                    {model.settings.map((scen, i) => {
                      if (scen.settings.length > 1) {
                        return (
                          <div style={{marginBottom: "16px"}} ref={this.setTxContent} key={scen.name}>
                            <p className="tree2 group-header" title={scen.description}>{scen.name}</p>
                            <span className="tree3">Sensitivity: </span>
                            {scen.settings.map(subscen => (
                              <label title={subscen.description} key={subscen}>
                              <input
                                onChange={this.handleModelSelection}
                                className="tree3"
                                name="slr-model"
                                type="radio"
                                value={subscen}
                                checked={modelChecked(subscen)}
                              />
                              {metadata.settings[subscen].name}
                              </label>
                            ))}
                          </div>
                        )
                      } else {
                        const subscen = scen.settings[0];
                        return (
                          <div key={subscen}>
                            <label title={subscen}>
                              <input
                                onChange={this.handleModelSelection}
                                className="tree3"
                                name="slr-model"
                                type="radio"
                                value={subscen}
                                checked={modelChecked(subscen)}
                              />
                              {metadata.settings[subscen].name}
                            </label>
                            <p className="tree4">{this.scenarioExplanatoryText(subscen, metadata.settings[subscen].name)}</p>
                          </div>
                        )
                      }
                    })}
                  </div>
                  : null
                }
              </div>
            )
          })}
          <h2 id="select-station">Select a water level station (tide gauge)</h2>
          {stations.map((station, i) => {
            return (
              <label key={station.id}>
                <input
                  type="radio"
                  id={station.id}
                  value={i}
                  name="tide-station"
                  checked={i === stationNum}
                  onChange={this.handleStationChange}/> <span><var>{xlt(station.name)}</var> (<var>{station.dist}</var> {distUnit} from central <var>{xlt(simplePlaceName(placeName))}</var></span>)
              </label>
            )
          })}
          <br />
        </Modal>

        <Modal
          isOpen={this.state.downloadOpen && this.props.modalVisible}
          handleClick={this.closeDownload}
        >
          <DownloadForm
            id="ForecastDLForm"
            placeType={place.type}
            placeID={place.id}
            forecastType={forecastType}
            dlType={this.state.dlType}
            productID="forecast"
            dlCallback={this.dlCallback}
          >
          </DownloadForm>
        </Modal>
      </div>
    );
  }

}

// Use connect solely for access to redux "dispatch";
// Don't use any state from the store. Such props will be passed directly.
export default compose(
  withStyles(styles),
  connect(() => {return {};}),
)(Forecast);
